
import React, { useMemo } from "react";
import { Card, CardBody, CardTitle, Button, Row, Col } from "reactstrap";

// ✅ use the new mapper + types
import { toolPageForProductCodeType, ToolPageKey } from "./typeToToolPage";

// ✅ use the new items-by-page source
import { TOOL_ITEMS_BY_PAGE, getBuyHref, WearItem } from "./toolItemsByPage";

import "./styles.scss";

type Props = {
  productCodeType?: string;
  serialNumber?: string;
  className?: string;
  heading?: string;     // Optional override
  subheading?: string;  // Optional override
};

const WearItemCard: React.FC<{ item: WearItem; href: string }> = ({ item, href }) => {
  const isDisabled = href === "#";

  return (
    <Card className="wear-card h-100">
      <CardBody className="d-flex flex-column align-items-center">
        <div className="wear-card__image">
          <img src={item.imageSrc} alt={item.title} loading="lazy" />
        </div>
        <CardTitle tag="h6" className="wear-card__title text-uppercase text-center mt-3">
          {item.title}
        </CardTitle>
        {item.description && (
          <p className="wear-card__descr text-muted text-center">{item.description}</p>
        )}
        <Button
          color="danger"
          className="wear-card__buy mt-auto"
          href={isDisabled ? undefined : href}
          target={isDisabled ? undefined : "_blank"}
          rel={isDisabled ? undefined : "noopener noreferrer"}
          disabled={isDisabled}
        >
          BUY NOW
        </Button>
      </CardBody>
    </Card>
  );
};

const WearPartsGrid: React.FC<Props> = ({
  productCodeType,
  serialNumber,
  className,
  heading,
  subheading,
}) => {
  // ✅ compute the tool page key from productCodeType
  const pageKey = useMemo<ToolPageKey | null>(
    () => toolPageForProductCodeType(productCodeType),
    [productCodeType]
  );

  if (!pageKey) return null; // or render a friendly fallback

  // ✅ fetch the page definition and its items
  const page = TOOL_ITEMS_BY_PAGE[pageKey];
  const itemsWithHref = page.items.map((item) => ({
    item,
    href: getBuyHref(item, serialNumber), // appends ?serial=<sn> or replaces :serialNumber
  }));

  return (
    <section className={`wear-grid ${className ?? ""}`}>
      <header className="wear-grid__header">
        <h3 className="wear-grid__title">{heading ?? page.title}</h3>
        <p className="wear-grid__subtitle text-muted">
          {subheading ?? page.subtitle ?? "Click to view"}
        </p>
      </header>

      <Row className="g-4">
        {itemsWithHref.map(({ item, href }) => (
          <Col key={item.id} xs="12" sm="6" md="4" lg="3">
            <WearItemCard item={item} href={href} />
          </Col>
        ))}
      </Row>
    </section>
  );
};

export default WearPartsGrid;
